/**
 * Class responsible for refreshing live rankings based on data-next-refresh times.
 * It handles setting up timers with a random delay for AJAX requests to update rankings.
 */
class LiveRankingRefresher {
    private elements: NodeListOf<HTMLElement>;

    /**
     * Initializes a new instance of the LiveRankingRefresher.
     * @param {string} selector - The CSS selector to find elements that require refreshing.
     */
    constructor(selector: string) {
        this.elements = document.querySelectorAll(selector);
        this.init();
    }

    /**
     * Sets up refresh timers for all selected elements based on their next refresh time.
     */
    private init(): void {
        this.elements.forEach(element => {
            const refreshTimeAttribute: HTMLElement | null = element.querySelector('[data-next-refresh]');
            const nextRefreshTime = this.parseNextRefreshTime(refreshTimeAttribute?.dataset.nextRefresh);

            if (nextRefreshTime) {
                const now = new Date();
                let delayMillis = nextRefreshTime.getTime() - now.getTime();
                delayMillis += this.getRandomDelay();

                setTimeout(() => this.refreshRanking(element), delayMillis);
            }
        });
    }

    /**
     * Parses the next refresh time from a time string.
     * @param {string | undefined} timeStr - The time string from data-next-refresh attribute.
     * @returns {Date | null} - The Date object of the next refresh time, or null if invalid.
     */
    private parseNextRefreshTime(timeStr: string | undefined): Date | null {
        if (!timeStr) return null;
        const parts = timeStr.split(':');
        const date = new Date();
        date.setHours(parseInt(parts[0], 10), parseInt(parts[1], 10), 0, 0);
        return date;
    }

    /**
     * Generates a random delay between 0 and 30 seconds to stagger the refresh requests.
     * @returns {number} - The number of milliseconds to delay the refresh.
     */
    private getRandomDelay(): number {
        return Math.floor(Math.random() * 10000);
    }

    /**
     * Performs an AJAX request to update the live ranking for a specific track.
     * @param {HTMLElement} element - The element representing a live ranking track.
     */
    private refreshRanking(element: HTMLElement): void {
        // @ts-ignore
        window.oc.ajax('onRequestLiveRankingUpdate', {
            data: {
                'trackNumber': element.dataset.trackNumber,
                'id': element.id,
            }
        });
    }
}

// Initialization code that runs when the document content is fully loaded.
document.addEventListener('DOMContentLoaded', () => {
    new LiveRankingRefresher('.live-pulling-rankings .ranking');
});